import { Badge } from '@/components/ui/badge'
import { Sparkles, MessageSquare } from 'lucide-react'

/**
 * Metadata structure for AI-generated content
 *
 * @property model - OpenAI model used (e.g., "gpt-4o-2024-11-20")
 * @property api_type - Which OpenAI API was used for generation
 * @property prompt_version - Version identifier of the prompt template
 * @property quality_score - Content quality score (0-100)
 * @property generated_at - ISO timestamp of when content was generated
 */
interface GenerationMetadata {
  model?: string
  api_type?: 'chat_completions' | 'responses'
  prompt_version?: string
  quality_score?: number
  generated_at?: string
}

interface AIGenerationBadgeProps {
  /** Metadata from the project writer worker */
  metadata?: GenerationMetadata
}

/**
 * AIGenerationBadge Component
 *
 * Displays which OpenAI API was used to generate project page content.
 * Shows either Responses API (newer, structured) or Chat Completions API (legacy).
 *
 * @example
 * ```tsx
 * <AIGenerationBadge metadata={projectPage.generation_metadata} />
 * ```
 *
 * @see /apps/production/project-writer-agent for worker implementation
 */
export function AIGenerationBadge({ metadata }: AIGenerationBadgeProps) {
  if (!metadata?.api_type) return null

  const isResponses = metadata.api_type === 'responses'

  return (
    <Badge
      variant={isResponses ? 'default' : 'secondary'}
      className="gap-1.5 px-2.5 py-1"
    >
      {isResponses ? (
        <Sparkles className="h-3 w-3" />
      ) : (
        <MessageSquare className="h-3 w-3" />
      )}
      <span className="font-medium">
        {isResponses ? 'Responses API' : 'Chat API'}
      </span>
      {metadata.model && (
        <span className="ml-1 opacity-70 text-xs font-normal">
          {metadata.model.replace('gpt-', 'GPT-')}
        </span>
      )}
    </Badge>
  )
}
